import React, { useContext, useMemo, useState } from 'react';
import { TranslationKey } from './../../../../../types/translation/TranslationKey';
import { CompanyContext } from '../../../../../context/CompanyContext';
import { t } from '../../../../../types/translation/Translator';
import CreateOrderInfoPane from './Panes/CreateOrderInfoPane';
import CreateOrderUserPane from './Panes/CreateOrderUserPane';
import { OrderContext } from '../../../../../context/OrderContext';
import CreateOrderFieldsPane from './Panes/CreateOrderFieldsPane';
import { testIds } from '../../../../../util/identifiers/identifiers.util';
import { ProductTransactionContext } from '../../../../../context/ProductTransactionContext';
import { ProductTransaction } from '../../../../../types/productTransaction';
import Modal from '../../../../../VentoryUI/components/common/Modal/Modal';
import { ContainerContext } from '../../../../../context/ContainerContext';
import CreateOrderContainerPane from './Panes/CreateOrderContainerPane';
import { ContainerUtil } from '../../../../../util/containers.util';
import { Order, OrderInput, OrderUtil } from '../../../../../types/order/';
import { DomUtils } from '../../../../../util/dom.util';
import CreateOrderProductInfoPane from './Panes/CreateOrderProductInfoPane';
import { Container } from '../../../../../types/container';
import { BinStatusContext } from '../../../../../context/BinStatusContext';
import { FlowType } from '../../../../../types/order/orderFlow';
import { OrderRequest } from '../../../../../requests/order.request';
import { useRequest } from '../../../../../requests/common/requestHook';
import { ProductTransactionRequest } from '../../../../../requests/productTransaction.request';

interface CreateOrderModalInputProps {
  open: boolean;
  setOpen: (value: boolean) => void;
}

enum CreateOrderState {
  info = 'info',
  container = 'container',
  product = 'product',
  fields = 'fields',
  user = 'user',
}

export default function CreateOrderModal({ open, setOpen }: CreateOrderModalInputProps) {
  if (!open) return null;

  const { currentCompany } = useContext(CompanyContext);
  const { orders, setOrders } = useContext(OrderContext);
  const { productTransactions, setProductTransactions } = useContext(ProductTransactionContext);
  const { containers } = useContext(ContainerContext);
  const { binStatuses } = useContext(BinStatusContext);

  const [orderInput, setOrderInput] = useState<OrderInput>(new OrderInput({ companyId: currentCompany.id }));

  const { inboundableContainers, outboundableContainers } = useMemo(() => {
    return ContainerUtil.getAvailableContainers(containers, orderInput);
  }, [productTransactions, containers, orderInput]);

  const validContainers: Container[] = useMemo(() => {
    switch (orderInput.type) {
      case FlowType.inbound:
      case FlowType.replenish:
        return inboundableContainers;
      case FlowType.outbound:
      case FlowType.move:
        return outboundableContainers;
      case FlowType.disposal:
        return outboundableContainers.filter(container => !binStatuses.get(container.id)?.outboundAllowed);
    }
  }, [inboundableContainers, outboundableContainers, orderInput, binStatuses]);

  const [currentPane, setCurrentPane] = useState<CreateOrderState>(CreateOrderState.info);
  const [error, setError] = useState<string>('');

  const [create, { loading }] = useRequest(OrderRequest.create);
  const [loadProductTransactions, { loading: productTransactionsLoading }] = useRequest(ProductTransactionRequest.list);

  const handleCreate = async () => {
    const result = await create({ data: [orderInput.forCreate()] });
    if (!result) return;

    const items = result;
    for (const order of items) {
      orders.set(order.id, new Order(order));
    }

    setOrders(new Map(orders));
    await fetchProductTransactions();
    setOpen(false);
  };

  const fetchProductTransactions = async () => {
    const response = await loadProductTransactions({ page: 0 });
    if (!response) return;

    const items = response.data;
    for (const item of items) {
      productTransactions.set(item.id, new ProductTransaction(item));
    }
    setProductTransactions(new Map(productTransactions));
  };

  const handleClose = () => {
    setCurrentPane(CreateOrderState.info);
    setOrderInput(new OrderInput(new OrderInput({ companyId: currentCompany.id })));
    setOpen(false);
  };

  const { productSelectionFirst, showContainerSelection } = useMemo(() => {
    const productSelectionFirst = currentCompany.settings.featureToggles.orders.productSelectionFirst;

    const showContainerSelection =
      currentCompany.settings.featureToggles.containers &&
      !currentCompany.settings.featureToggles.orders.productBasedApproach;

    return { productSelectionFirst, showContainerSelection };
  }, [currentCompany]);

  const handleNext = () => {
    switch (currentPane) {
      case CreateOrderState.info:
        if (showContainerSelection) {
          if (orderInput.type === FlowType.inbound) {
            return setCurrentPane(CreateOrderState.container);
          }
          if (OrderUtil.hasFromStockLocation(orderInput) && validContainers.length) {
            return setCurrentPane(CreateOrderState.container);
          }
        }

        return setCurrentPane(CreateOrderState.product);
      case CreateOrderState.container:
        return setCurrentPane(CreateOrderState.product);
      case CreateOrderState.product:
        if (productSelectionFirst) return setCurrentPane(CreateOrderState.fields);
        if (orderInput.fromStockLocationId?.includes('tag::')) return handleFinish();
        return setCurrentPane(CreateOrderState.user);
      case CreateOrderState.fields:
        return setCurrentPane(CreateOrderState.user);
    }
  };

  const handleFinish = async () => {
    await handleCreate();
  };

  const handleBack = () => {
    switch (currentPane) {
      case CreateOrderState.info:
        return;
      case CreateOrderState.container:
        return setCurrentPane(CreateOrderState.info);
      case CreateOrderState.product:
        if (showContainerSelection) {
          if (orderInput.type === FlowType.inbound) {
            return setCurrentPane(CreateOrderState.container);
          }
          if (OrderUtil.hasFromStockLocation(orderInput) && validContainers.length) {
            return setCurrentPane(CreateOrderState.container);
          }
        }

        return setCurrentPane(CreateOrderState.info);
      case CreateOrderState.fields:
        return setCurrentPane(CreateOrderState.product);
      case CreateOrderState.user:
        if (productSelectionFirst) return setCurrentPane(CreateOrderState.fields);
        return setCurrentPane(CreateOrderState.product);
    }
  };

  const modalHeight = DomUtils.getModalHeight();
  const content = () => {
    switch (currentPane) {
      case CreateOrderState.info:
        return <CreateOrderInfoPane order={orderInput} setOrder={setOrderInput} next={handleNext} />;
      case CreateOrderState.container:
        return (
          <CreateOrderContainerPane
            order={orderInput}
            setOrder={setOrderInput}
            next={handleNext}
            validContainers={validContainers}
            setError={setError}
            back={handleBack}
          />
        );
      case CreateOrderState.product:
        return (
          <CreateOrderProductInfoPane
            order={orderInput}
            setOrder={setOrderInput}
            setError={setError}
            next={handleNext}
            back={handleBack}
            loading={loading}
            validContainers={validContainers}
            modalHeight={modalHeight}
          />
        );
      case CreateOrderState.fields:
        return (
          <CreateOrderFieldsPane order={orderInput} setOrder={setOrderInput} next={handleNext} back={handleBack} />
        );
      case CreateOrderState.user:
        return (
          <CreateOrderUserPane
            order={orderInput}
            setOrder={setOrderInput}
            next={handleFinish}
            back={handleBack}
            loading={loading || productTransactionsLoading}
          />
        );
    }
  };

  return (
    <Modal
      error={error}
      open={open}
      onClose={handleClose}
      height={modalHeight}
      title={t(TranslationKey.createOrder).singular.label}
      testId={testIds.createOrderModal}
    >
      {content()}
    </Modal>
  );
}
